// Routes policy hydration to a specific aigent-service Cloud Run env via a
// URL query flag (e.g. ?dev1, ?ppe). The choice is persisted in sessionStorage
// so it survives SPA route changes — auth redirects and react-router
// navigation can strip the query string before the policies page mounts.
//
// `captureBackendEnvFromUrl` MUST be called at app boot (from index.tsx),
// before any router renders, so the param is saved before navigation can
// drop it. `getBackendEnvOverride` is called at policy-save time and prefers
// the current URL but falls back to the stored value.

export const SUPPORTED_BACKEND_ENVS = ["prod", "ppe", "dev1", "dev2", "dev3"] as const;
export type BackendEnv = (typeof SUPPORTED_BACKEND_ENVS)[number];

const STORAGE_KEY = "aigent_backend_env";

function readFromUrl(): BackendEnv | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  for (const env of SUPPORTED_BACKEND_ENVS) {
    if (params.has(env)) return env;
  }
  return null;
}

function readFromStorage(): BackendEnv | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    if (stored && (SUPPORTED_BACKEND_ENVS as readonly string[]).includes(stored)) {
      return stored as BackendEnv;
    }
  } catch {
    // sessionStorage may be unavailable (private mode, etc.)
  }
  return null;
}

function writeToStorage(env: BackendEnv): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, env);
  } catch {
    // ignore
  }
}

/** Called once at app boot from index.tsx. Captures the query flag before
 *  any router navigation strips it. Safe to call multiple times. */
export function captureBackendEnvFromUrl(): void {
  const fromUrl = readFromUrl();
  if (fromUrl) writeToStorage(fromUrl);
}

/** Called at policy-save time. Prefers the current URL (the user may have
 *  just appended a flag without reloading), falls back to the stored value
 *  set at boot. Returns null when no override is active. */
export function getBackendEnvOverride(): BackendEnv | null {
  const fromUrl = readFromUrl();
  if (fromUrl) {
    writeToStorage(fromUrl);
    return fromUrl;
  }
  return readFromStorage();
}
